import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { AuthUser } from '@yoreflix/core/api/types'
import { Topbar } from './components/Topbar'
import { SideMenu } from './components/SideMenu'
import { FilterBar } from './components/FilterBar'
import { BusyButton } from './components/BusyButton'
import { landingItem, resolveRoute, visibleNav } from './nav/navModel'
import { useHashRoute } from './nav/useHashRoute'
import { useLayout, usePanel } from './nav/useBreakpoint'
import type { SliceAxes, SliceDepth, SliceScope, SliceType } from './analytics/slice'
import { Organization } from './screens/Organization'
import { OverviewScreen } from './screens/Overview'
import { TitlesScreen } from './screens/Titles'
import { SuppliedScreen } from './screens/Supplied'
import { providerClient } from './provider/providerClient'
import type { PeriodOption, ProviderOverview, TitleRow } from './provider/providerClient'

/**
 * DashboardShell — the same L of chrome as the admin portal (topbar across the top, side menu
 * down the left) around a scrolling page area, and deliberately the SAME design system: tokens,
 * `.admin-btn*` and `.admin-option` hover contracts copied from `apps/admin` verbatim (David,
 * 2026-07-23: "use the same colour scheme for dashboard as admin"). Two portals that looked like
 * two products would be a worse outcome than either look on its own.
 *
 * ★★ SCREENS ARE MOUNTED ONCE AND KEPT, exactly as admin does — leaving a screen hides it rather
 * than destroying it, so its selections, filters and scroll position are where you left them
 * when you come back (David, 2026-07-23). Built in from the start here rather than retrofitted.
 *
 * ⚠ NO SAVE/CANCEL. Admin's shell routes an editor controller per screen because admin AUTHORS
 * things; this portal is read-only by posture, so the topbar carries no dirty state and there is
 * no editor plumbing to get wrong. If that ever changes, it is a decision to revisit — not a
 * gap to fill in quietly.
 */

/**
 * ⚠ LAYOUT LIVES IN `styles/layout.css`, NOT HERE. Inline styles cannot carry a media query and
 * they BEAT any class that tries to override them, so a layout property written in this file is a
 * layout property that will not respond on a phone. Colour and background stay; arrangement goes.
 */
const pageArea: CSSProperties = { background: 'var(--background)' }

const userChip: CSSProperties = {
  fontSize: 'var(--fs-sm)',
  color: 'var(--muted-foreground)',
  maxWidth: 220,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const signOutBtn: CSSProperties = {
  border: '1px solid var(--border)',
  color: 'var(--foreground)',
  borderRadius: 'var(--radius)',
  padding: '5px 10px',
  fontSize: 'var(--fs-sm)',
  cursor: 'pointer',
}

const brand: CSSProperties = { fontWeight: 700, fontSize: 'var(--fs-title)', flex: '0 0 auto', paddingRight: 4 }

function query(route: string): URLSearchParams {
  return new URLSearchParams(route.split('?')[1] ?? '')
}

/**
 * ⚠ AN UNKNOWN VALUE IN THE URL FALLS BACK, IT DOES NOT THROW AND IT DOES NOT PASS THROUGH. The
 * hash is user-editable and gets shared around; a typo must land you on a sane default rather than
 * on a slice whose meaning nobody can name.
 */
function oneOf<T extends string>(raw: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.includes((raw ?? '') as T) ? ((raw ?? '') as T) : fallback
}

const TYPES = ['all', 'films', 'series', 'supporting'] as const satisfies readonly SliceType[]
const DEPTHS = ['title', 'season', 'episode', 'supporting'] as const satisfies readonly SliceDepth[]
const SCOPES = ['yours', 'platform'] as const satisfies readonly SliceScope[]

function readAxes(route: string): SliceAxes {
  const q = query(route)
  return {
    type: oneOf(q.get('type'), TYPES, 'all'),
    depth: oneOf(q.get('depth'), DEPTHS, 'title'),
    scope: oneOf(q.get('scope'), SCOPES, 'yours'),
  }
}

/** Mounted on first visit, hidden thereafter. See the note above. */
function Slot({ active, children }: { active: boolean; children: ReactNode }) {
  return <div style={{ height: '100%', display: active ? 'block' : 'none' }}>{children}</div>
}

export function DashboardShell({ user, onSignOut }: { user: AuthUser; onSignOut: () => void }) {
  const layout = useLayout()
  const menu = usePanel(layout)
  const { route, navigate } = useHashRoute()

  const groups = useMemo(() => visibleNav(user), [user])
  const landing = useMemo(() => landingItem(user), [user])
  const active = useMemo(() => resolveRoute(route, user), [route, user])

  // An unknown, forbidden or not-yet-built route falls back to the landing item. Replace rather
  // than push, so Back doesn't bounce the user straight into the bad route again.
  useEffect(() => {
    if (!active && landing) window.location.replace(`#/${landing.id}`)
  }, [active, landing])

  const current = active ?? landing
  const currentId = current?.id ?? null

  // Escape closes the overlay menu — the other half of the scrim's job, for anyone on a keyboard.
  useEffect(() => {
    if (!menu.overlay || !menu.open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') menu.close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menu])

  const [visited, setVisited] = useState<ReadonlySet<string>>(() => new Set())
  useEffect(() => {
    if (currentId) setVisited((prev) => (prev.has(currentId) ? prev : new Set(prev).add(currentId)))
  }, [currentId])

  /**
   * ★★ ONE QUERY STATE FOR THE WHOLE PAGE (plan §6.0, v1's shape and the right one): the period
   * lives HERE, not inside a screen, so Overview and Your titles can never be showing two
   * different windows at once. Changing it refetches both.
   *
   * ★ It is reflected in the URL so a view is bookmarkable — cheap built in from the start,
   * painful to retrofit. `#/overview?period=previous-quarter`.
   */
  const [periods, setPeriods] = useState<PeriodOption[]>([])
  const [period, setPeriod] = useState<string>(() => query(route).get('period') ?? '')
  /**
   * ★★ ALL FOUR AXES IN ONE OBJECT, IN ONE PLACE. v1's dashboard is worth copying for the way its
   * control bar re-cuts every panel at once; it is worth NOT copying for how it stores that state,
   * because its two tabs share a period while each draws its own selector, and the two drift.
   * There is exactly one of these, every panel reads it, and it is reflected in the URL so a slice
   * is bookmarkable: `#/titles?period=q3-2026&type=series&depth=episode&scope=platform`.
   */
  const [axes, setAxes] = useState<SliceAxes>(() => readAxes(route))
  const [overview, setOverview] = useState<ProviderOverview | null>(null)
  const [titles, setTitles] = useState<TitleRow[]>([])
  /**
   * ⚠⚠ THE LABEL SHIPS WITH THE DATA IT LABELS — never with the control that requested it.
   * Live v1 has the opposite: its money panel is headed "Revenue Share for last Quarter
   * (April - June)" from a static string while the figures follow a period selector on the
   * OTHER tab, so a supplier can be shown a different quarter's royalty under the right
   * quarter's heading (£29.57 vs £52.42 for the same org, 2026-07-23). Here the titles table's
   * period caption comes from the TITLES response, not the overview's, so the caption and the
   * rows cannot come from different windows even if the two fetches ever diverge.
   */
  const [titlesPeriodLabel, setTitlesPeriodLabel] = useState('')
  const [analyticsError, setAnalyticsError] = useState('')

  useEffect(() => {
    providerClient
      .periods()
      .then((p) => {
        setPeriods(p.Periods)
        setPeriod((current) => (current !== '' ? current : p.Default))
      })
      .catch(() => setAnalyticsError('Could not load the reporting periods.'))
  }, [])

  // ⚠ Both surfaces are fetched together, from ONE period, so a supplier can never compare a
  // headline figure against a table that answered about a different window.
  useEffect(() => {
    if (period === '') return
    let cancelled = false
    setAnalyticsError('')
    Promise.all([providerClient.overview(period), providerClient.titles(period)])
      .then(([o, t]) => {
        if (cancelled) return
        setOverview(o)
        setTitles(t.Titles ?? [])
        setTitlesPeriodLabel(t.Period?.Label ?? '')
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setAnalyticsError(e instanceof Error ? e.message : 'Could not load your figures.')
      })
    return () => {
      cancelled = true
    }
  }, [period])

  // Replace rather than push: flicking through slices should not fill the Back button.
  const writeHash = useCallback(
    (key: string, next: SliceAxes) => {
      if (!currentId) return
      const q = new URLSearchParams({ period: key, type: next.type, depth: next.depth, scope: next.scope })
      window.location.replace(`#/${currentId}?${q.toString()}`)
    },
    [currentId],
  )

  const choosePeriod = useCallback(
    (key: string) => {
      setPeriod(key)
      writeHash(key, axes)
    },
    [axes, writeHash],
  )

  const chooseAxes = useCallback(
    (next: SliceAxes) => {
      setAxes(next)
      writeHash(period, next)
    },
    [period, writeHash],
  )

  const analytics = currentId === 'overview' || currentId === 'titles'

  const screens: { id: string; node: ReactNode }[] = [
    {
      id: 'overview',
      node: <OverviewScreen data={overview} titles={titles} axes={axes} error={analyticsError} />,
    },
    {
      id: 'titles',
      node: (
        <TitlesScreen
          rows={titles}
          period={period}
          periodLabel={titlesPeriodLabel}
          axes={axes}
          platformWatchSeconds={overview?.Platform?.WatchSeconds ?? 0}
          error={analyticsError}
          linked={overview?.Linked !== false}
        />
      ),
    },
    { id: 'supplied', node: <SuppliedScreen /> },
    { id: 'profile', node: <Organization /> },
  ]

  return (
    <div className="dash-shell">
      <Topbar
        brand={<span style={brand}>Yoreflix</span>}
        title={current?.label}
        onMenuToggle={menu.toggle}
        actions={
          <>
            <span style={userChip} title={user.EmailAddress}>
              {user.DisplayName || user.EmailAddress}
            </span>
            {/* Same ~3s round trip as the no-access screen's button — same treatment. */}
            <BusyButton className="admin-btn" style={signOutBtn} onClick={onSignOut} busyLabel="Signing out…">
              Sign out
            </BusyButton>
          </>
        }
      />
      <div className="dash-body">
        {/* ★★ A PANE AT DESKTOP, A SLIDE-OUT OVERLAY BELOW IT (David: "menu becomes slide out -
            select option - auto side back on mobile"). In overlay mode the menu is always rendered
            at full width and the WRAPPER slides it in and out, so the width-collapse animation and
            the slide animation never fight each other. */}
        <div className="dash-menu" data-open={menu.open ? 'true' : 'false'}>
          <SideMenu
            groups={groups}
            activeId={current?.id ?? null}
            open={menu.overlay ? true : menu.open}
            onNavigate={(item) => {
              navigate(item.id)
              // ⚠ CLOSE FIRST, and let the incoming screen render its frame immediately. Closing
              // after the data lands reads as a failed tap — the same reason a drop has to land
              // before its request does.
              menu.closeIfOverlay()
            }}
            buildStamp={__BUILD_STAMP__}
          />
        </div>
        {/* ⚠ MOUNTED FOR THE WHOLE OF OVERLAY MODE, not just while open — a scrim that unmounts on
            close has nothing left on screen to fade out. Opacity is driven by `data-open`, and the
            two durations live in CSS: 2s to darken in, 600ms to lift on the way back (David). When
            closed it is transparent and `pointer-events:none`, so it never intercepts a tap. */}
        {menu.overlay && (
          <button
            type="button"
            className="dash-scrim"
            data-open={menu.open ? 'true' : 'false'}
            aria-label="Close menu"
            aria-hidden={!menu.open}
            tabIndex={menu.open ? 0 : -1}
            onClick={menu.close}
          />
        )}
        <main className="dash-main" style={pageArea}>
          {/* ⚠ The control bar belongs to the analytics screens only. On the ledger or the
              organization page a period selector would be a control that changes nothing. */}
          {analytics && periods.length > 0 && (
            <FilterBar
              periods={periods}
              period={period}
              axes={axes}
              onPeriod={choosePeriod}
              onAxes={chooseAxes}
            />
          )}
          <div style={{ flex: '1 1 auto', minHeight: 0 }}>
          {screens
            .filter((s) => s.id === currentId || visited.has(s.id))
            .map((s) => (
              <Slot key={s.id} active={s.id === currentId}>
                {s.node}
              </Slot>
            ))}
          </div>
        </main>
      </div>
    </div>
  )
}
